import { useTranslation } from '../presentation/common/useTranslation.js';
import Skeleton from './Skeleton';
import SkeletonDelay from './SkeletonDelay';
import { partnerDetailLayout as layout } from './partnerDetailLayout';
import '../pages/PartnerDetail.css';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useLocation } from 'react-router-dom';

function Action({ children, className = '', iconSize = 16 }) {
  return <span className={`world-button relative ${className}`}>
    <span className="invisible shrink-0" style={{ width: iconSize, height: iconSize }} />
    <span className="invisible">{children}</span>
    <Skeleton className="absolute inset-0 rounded-full" />
  </span>;
}

function Text({ className = '' }) {
  return <div className={`space-y-3 ${className}`}><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-2/3" /></div>;
}

export default function PartnerDetailSkeleton(props) {
  return <SkeletonDelay><PartnerDetailSkeletonContent {...props} /></SkeletonDelay>;
}

function PartnerDetailSkeletonContent({ className = '' }) {
  const { t } = useTranslation();
  const { isLoggedIn, isAdventurer } = useCurrentUser();
  const { pathname } = useLocation();
  const canRedeem = isLoggedIn && isAdventurer && !pathname.startsWith('/partner/public/');
  return <div className={`${layout.page} page-skeleton partner-detail-skeleton ${className}`}
    role="status" aria-busy="true" aria-label={t('common.loading', 'Lädt …')} data-skeleton="partner-detail">
    <span className="skeleton-sr-only">{t('common.loading', 'Lädt …')}</span>
    <div className={layout.navigation} aria-hidden="true">
      <Action className="min-h-[44px] inline-flex items-center gap-2 text-sm !py-2 !px-3.5">{t('partnerDetail.back', 'Zurück')}</Action>
      <div className="flex flex-wrap items-center gap-2">
        {canRedeem && <Action className="text-xs">{t('redemptionHistory.title')}</Action>}
        <Action iconSize={15} className="min-h-[44px] inline-flex items-center gap-2 text-xs !py-2 !px-3"><span className="hidden sm:inline">{t('partnerDetail.view_on_map', 'Auf Weltkarte zeigen')}</span></Action>
        {canRedeem && <Action className="min-h-[44px] inline-flex items-center gap-2 text-xs font-bold !py-2 !px-3.5">{t('partnerDetail.show_qr_pass', 'Mein Vor-Ort QR-Pass')}</Action>}
      </div>
    </div>
    <div className={layout.hero} aria-hidden="true">
      <div className={layout.banner}><Skeleton className="absolute inset-0 w-full h-full !rounded-none" /></div>
      <div className={layout.identity}>
        <div className="relative shrink-0 -mt-16 sm:-mt-14"><Skeleton className="w-28 h-28 rounded-2xl" /></div>
        <div className="flex-1 space-y-2.5 min-w-0 pb-1 w-full">
          <div className="flex flex-wrap justify-center md:justify-start gap-2.5"><Skeleton className="h-6 w-60 rounded-full" /><Skeleton className="h-6 w-40 rounded-full" /></div>
          <Skeleton className="h-8 sm:h-9 md:h-10 w-64 max-md:mx-auto" />
          <div className="flex flex-wrap justify-center md:justify-start gap-4"><Skeleton className="h-4 w-40" /><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-48" /></div>
          <Skeleton className="h-4 w-3/4 max-md:mx-auto !mt-3" />
        </div>
      </div>
    </div>
    <div className={layout.columns} aria-hidden="true">
      <div className={layout.offers}>
        <div className="pb-2 border-b border-zinc-800"><Skeleton className="h-7 w-52" /></div>
        <div className="partner-offer-card world-panel p-5 space-y-4">
          <div className="flex items-start gap-3"><Skeleton className="w-10 h-10 rounded-xl" /><div className="flex-1 min-w-0 space-y-2"><Skeleton className="h-6 w-2/5" /><Skeleton className="h-4 w-3/4" /></div><Skeleton className="h-6 w-24" /></div>
          <div className="p-3 bg-zinc-950 rounded-lg"><Text /></div>
          <Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-40" /><Skeleton className="h-11 w-32" />
          <div className="pt-3 border-t border-zinc-800"><Skeleton className="h-3 w-24" /></div>
        </div>
      </div>
      <div className="space-y-6">
        <div className="partner-detail-about world-panel p-5 space-y-4"><Skeleton className="h-6 w-48" /><Text /></div>
        <div className="partner-detail-location world-panel p-5 space-y-4"><Skeleton className="h-6 w-48" /><div className="p-3 bg-zinc-950 rounded-lg"><Text /></div><Skeleton className="h-11 w-full rounded-full" /><Skeleton className="h-11 w-full rounded-full" /></div>
      </div>
    </div>
  </div>;
}
